import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { EditorViewTabs } from '@/components/editorViewTabs/EditorViewTabs';

const VIEW_LABELS = {
  scene: 'Scene View',
  editor: 'Editor View',
  workflow: 'Workflow',
};

/**
 * Shared top chrome for Scene / Editor / Workflow pages.
 * Left: Dashboard + title · Center: view tabs · Right: page actions
 */
export function EditorTopNav({
  active = 'scene',
  mode = 'story',
  projectId,
  storyId,
  title = '',
  subtitle = '',
  loading = false,
  onBeforeNavigate,
  onBack,
  backLabel = 'Dashboard',
  actions = null,
  className = '',
}) {
  const navigate = useNavigate();
  const viewLabel = VIEW_LABELS[active] || 'Editor';

  const handleBack = () => {
    if (typeof onBack === 'function') {
      onBack();
      return;
    }
    navigate('/dashboard');
  };

  return (
    <header
      className={`w-full h-12 px-4 sm:px-6 flex items-center gap-3 border-b border-[#1e272e] bg-[#0b1015]/90 backdrop-blur-md shrink-0 z-20 relative font-jakarta ${className}`}
      data-testid="editor-top-nav"
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <button
          type="button"
          onClick={handleBack}
          className="h-8 px-2.5 rounded-lg border border-white/10 bg-white/5 text-slate-300 hover:text-white text-xs inline-flex items-center gap-1.5 cursor-pointer shrink-0"
          data-testid="editor-top-nav-back"
        >
          <ArrowLeft className="w-3.5 h-3.5 text-[#62FAE3]" />
          <span className="hidden sm:inline">{backLabel}</span>
        </button>

        <div className="min-w-0 hidden xs:block sm:block">
          <p className="text-[10px] font-mono uppercase tracking-widest text-[#9CA3AF] truncate">
            {viewLabel}
            {subtitle ? ` · ${subtitle}` : ''}
          </p>
          <p className="text-sm font-bold text-white truncate flex items-center gap-2 leading-tight">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin text-[#62FAE3] shrink-0" /> : null}
            <span className="truncate">{title || (loading ? 'Loading…' : 'Untitled')}</span>
          </p>
        </div>
      </div>

      <div className="absolute left-1/2 -translate-x-1/2 hidden md:flex pointer-events-none">
        <div className="pointer-events-auto">
          <EditorViewTabs
            active={active}
            mode={mode}
            projectId={projectId}
            storyId={storyId}
            onBeforeNavigate={onBeforeNavigate}
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 min-w-0 flex-1">
        <div className="md:hidden shrink-0">
          <EditorViewTabs
            active={active}
            mode={mode}
            projectId={projectId}
            storyId={storyId}
            onBeforeNavigate={onBeforeNavigate}
          />
        </div>
        {actions ? (
          <div className="flex items-center gap-2 shrink-0 min-w-0 overflow-x-auto scrollbar-none">
            {actions}
          </div>
        ) : null}
      </div>
    </header>
  );
}

export default EditorTopNav;
